import { cloneElement, type ReactElement } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export interface PermissionGuardProps {
  /** When false, the wrapped control is disabled and a tooltip explains why. */
  allowed: boolean;
  /** Tooltip text shown when the action is blocked, e.g. "Only owners and admins can approve bills". */
  reason: string;
  /** Shown instead of `reason` in a demo, where the role isn't what stops it (`usePermissions().demoReason`). */
  demoReason?: string | null;
  /** A single interactive element (e.g. a `<Button>`) that accepts a `disabled` prop. */
  children: ReactElement<{ disabled?: boolean }>;
  /**
   * A disabled button swallows pointer events, so the tooltip fires on this wrapper. Defaults to `inline-block`;
   * pass `"w-full"` for full-width buttons.
   */
  className?: string;
}

/** Optimistic: the backend 403 is the real enforcement; this disables with a tooltip. */
export function PermissionGuard({
  allowed,
  reason,
  demoReason,
  children,
  className = "inline-block",
}: PermissionGuardProps) {
  if (allowed) return children;

  const disabledChild = cloneElement(children, { disabled: true });

  return (
    <TooltipProvider>
      <Tooltip>
        {/* Wrapper span lets the tooltip still trigger on a disabled control. */}
        <TooltipTrigger asChild>
          <span tabIndex={0} className={className}>
            {disabledChild}
          </span>
        </TooltipTrigger>
        <TooltipContent>{demoReason ?? reason}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
